import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MultiSelect } from "primereact/multiselect";
import { MdArrowBack } from "react-icons/md";
import { FaRegCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import * as apicall from "../../components/Apicall";
import BookingCalendar from "./BookingCalendar";
import AppointmentSidebar from "../../components/Appointment/AppointmentSidebar";
import { getBookingLabel } from "../../utils/industryLabels";
import { addMinutesToDateTime } from "../../utils/bookingTime";
import MobileDayAgenda from "../../components/Dashboard/MobileDayAgenda";
import useViewport from "../../hooks/useViewport";

// Same status-name normalizer/rules Dashboard.jsx uses for clinic bookings,
// kept in sync here so a booking's color on this page always matches its
// color on the clinic dashboard's tiles/donut.
const normStatus = (s) => (s || "").toString().toLowerCase().replace(/[^a-z]/g, "");
const CLINIC_STATUS_COLOR_RULES = [
  { test: (n) => n.includes("noshow"), colors: { bgColor: "#FFF4E5", borderColor: "#FD7E14", textColor: "#FD7E14" } },
  { test: (n) => n.includes("cancel"), colors: { bgColor: "rgba(255, 204, 204, 0.5)", borderColor: "#FF0000", textColor: "#CC0000" } },
  { test: (n) => n.includes("checkout") || n.includes("checkedout") || n === "completed", colors: { bgColor: "rgba(204, 255, 226, 0.5)", borderColor: "#28A745", textColor: "#1E7E34" } },
  { test: (n) => n.includes("checkin") || n.includes("checkedin"), colors: { bgColor: "rgba(255, 255, 204, 0.5)", borderColor: "#CCCC00", textColor: "#666600" } },
  { test: (n) => n.includes("progress"), colors: { bgColor: "#FFF0FD", borderColor: "#92007E", textColor: "#92007E" } },
];
const DEFAULT_CLINIC_STATUS_COLORS = { bgColor: "#FFF0FD", borderColor: "#92007E", textColor: "#92007E" };

// Same numeric-status-id colors NewDashboard1.jsx uses for every other
// industry, kept in sync here for the same reason as above.
const NUMERIC_STATUS_COLORS = {
  10: { bgColor: "#FDEDED", borderColor: "#FF0000", textColor: "#FF0000" },
  1: { bgColor: "rgba(255, 255, 204, 0.5)", borderColor: "#CCCC00", textColor: "#666600" },
  2: { bgColor: "#FFF0FD", borderColor: "#92007E", textColor: "#92007E" },
  3: { bgColor: "rgba(255, 204, 204, 0.5)", borderColor: "#CC0000", textColor: "#990000" },
  4: { bgColor: "rgba(255, 255, 204, 0.5)", borderColor: "#CCCC00", textColor: "#666600" },
  12: { bgColor: "F2F9FF", borderColor: "#037FE8", textColor: "#037FE8" },
  default: { bgColor: "rgba(230, 230, 230, 0.5)", borderColor: "#999999", textColor: "#666666" },
};
const NUMERIC_STATUS_NAMES = { 4: "Completed", 2: "In Progress", 3: "Cancelled" };

// Full-page replacement for the "View Calendar" popup that used to live on
// NewDashboard1.jsx / Dashboard.jsx — reachable from its own sidebar tab.
// Reads the date/provider filter the dashboard was showing (via navigate
// state) so it opens with the same data already on screen, and otherwise
// falls back to today / no filter.
const CalendarPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Phones get a day agenda instead of the resource time-grid (see MobileDayAgenda).
  const { isPhone } = useViewport();

  const [selectedDate, setSelectedDate] = useState(() => {
    if (location.state?.selectedDate) return location.state.selectedDate;
    const today = new Date();
    const d1 = today.toLocaleDateString("en-GB").split(/[\/\-.]/);
    return `${d1[2]}-${d1[1]}-${d1[0]}`;
  });
  const [selectedProvider, setSelectedProvider] = useState(location.state?.selectedProvider || []);
  const [open, setOpen] = useState(false);
  const dateFilterRef = useRef(null);
  const [initialData, setInitialData] = useState([]);
  const [fullData, setFullData] = useState([]);
  const [allCrews, setAllCrews] = useState([]);
  const [filteredCrews, setFilteredCrews] = useState([]);
  const [industry, setIndustry] = useState(null);
  const [showCanvas, setShowCanvas] = useState(false);
  const [canvasType, setCanvasType] = useState("");
  const [editGroupId, setEditGroupId] = useState(null);
  const lastRequestId = useRef(0);

  // Close the "Filter by" date popup on an outside click - it otherwise
  // stays open until a date is picked or the calendar button is clicked again.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apicall.get("ezpMyProfile");
        const profile = response?.data?.[0];
        if (profile) setIndustry(profile);
      } catch (e) {
        console.error("Failed to fetch profile:", e);
      }
    };
    fetchProfile();
  }, []);

  const isClinicService = industry?.industry === 1;
  const isSportsService = industry?.industry === 7;
  const isCarService = industry?.industry === 8;
  const isSalonService = industry?.industry === 9;
  const crewLabel = isClinicService
    ? "Doctor"
    : isSportsService
    ? "Coach"
    : isCarService || isSalonService
    ? "Service Provider"
    : "Crew";

  const filterByProviders = (bookings, crews, providers) => {
    if (!providers.length) return { filteredBookings: bookings, filteredCrewsList: crews };
    const names = providers.map((p) => p.name.trim());
    const filteredBookings = bookings.filter((item) => {
      const providerList = item.provider_names ? item.provider_names.split(",").map((p) => p.trim()) : [];
      return providerList.some((p) => names.includes(p));
    });
    const filteredCrewsList = crews.filter((crew) => names.includes(crew.first_name));
    return { filteredBookings, filteredCrewsList };
  };

  const getBookingByDate = async () => {
    try {
      if (!selectedDate) return;
      const currentId = ++lastRequestId.current;
      const [bookingRes, crewRes] = await Promise.all([
        apicall.get(`get_ezp_booking?date=${selectedDate}`),
        apicall.get(`get_vendor_crews`),
      ]);
      if (currentId !== lastRequestId.current) return;

      const allData = bookingRes?.data || [];
      const a1 = allData.filter((ele) => ele?.booking_status !== "Cancelled");
      const crews = crewRes?.data || [];
      const { filteredBookings, filteredCrewsList } = filterByProviders(a1, crews, selectedProvider);

      setInitialData(a1);
      setFullData(filteredBookings);
      setAllCrews(crews);
      setFilteredCrews(filteredCrewsList);
    } catch (e) {
      console.log("getBookingByDate err----", e);
    }
  };

  useEffect(() => {
    getBookingByDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const toFullCalendarDate = (bookingDate, bookingTime) => `${bookingDate}T${bookingTime}:00`;

  const getClinicStatusColors = (statusName) => {
    const n = normStatus(statusName);
    const rule = CLINIC_STATUS_COLOR_RULES.find((r) => r.test(n));
    return rule ? rule.colors : DEFAULT_CLINIC_STATUS_COLORS;
  };
  const getNumericStatusColors = (status) => NUMERIC_STATUS_COLORS[status] || NUMERIC_STATUS_COLORS.default;

  const calenderData = fullData?.flatMap((item) => {
    const colors = isClinicService
      ? getClinicStatusColors(item?.booking_status)
      : getNumericStatusColors(item?.status);

    const start = toFullCalendarDate(item.service_date, item.service_time);
    const providerNames = item.provider_names ? item.provider_names.split(",").map((p) => p.trim()) : [];
    const providerIds = item.provider_ids
      ? item.provider_ids.split(",").map((p) => p.trim()).filter(Boolean)
      : [];

    const timePerPerson =
      (Number(item?.total_time_required) + Number(item?.total_buffer)) / (providerIds.length || 1);
    // Full date+time so jobs running past midnight end on the next day
    // instead of wrapping to a clock time before the start.
    const end =
      addMinutesToDateTime(item.service_date, item.service_time, timePerPerson) ||
      start;

    item.status_name = isClinicService
      ? item.booking_status || "Booked"
      : NUMERIC_STATUS_NAMES[item.status] || "In Progress";

    if (!providerIds.length) {
      return [
        {
          id: `${item.booking_id}-unassigned`,
          title: "Unassigned",
          resourceId: "unassigned",
          start,
          end,
          backgroundColor: colors.bgColor,
          borderColor: colors.borderColor,
          textColor: colors.textColor,
          extendedProps: { ...item, provider_name: "Unassigned", provider_id: null },
        },
      ];
    }

    return providerIds.map((id, index) => {
      const matchedCrew = allCrews.find((c) => String(c.user_id).trim() === String(id).trim());
      const title = matchedCrew?.first_name || providerNames[index] || "Crew";
      return {
        id: `${item.booking_id}-${id}`,
        title,
        resourceId: id,
        start,
        end,
        backgroundColor: colors.bgColor,
        borderColor: colors.borderColor,
        textColor: colors.textColor,
        extendedProps: { ...item, provider_name: title, provider_id: id },
      };
    });
  });

  const handleAppointmentClick = (data) => {
    const group_id = data?.details?.display_booking_id || 1;
    if (isClinicService) {
      setEditGroupId(group_id);
      setCanvasType("edit");
      setShowCanvas(true);
    } else {
      navigate(`/vendor_bookings/${group_id}`);
    }
  };

  const datesSet = (dateInfo) => {
    try {
      const d1 = dateInfo.startStr?.split("T")?.[0];
      if (d1) setSelectedDate(d1);
    } catch (e) {
      console.log("datesSet err----", e);
    }
  };

  const onProviderSearch = (providers) => {
    setSelectedProvider(providers);
    const { filteredBookings, filteredCrewsList } = filterByProviders(initialData, allCrews, providers);
    setFullData(filteredBookings);
    setFilteredCrews(filteredCrewsList);
  };

  const allProvider = [
    ...new Set([
      ...(initialData?.flatMap((item) => item.provider_names?.split(",").map((n) => n.trim())) || []),
      ...(allCrews?.map((crew) => crew.first_name?.trim()) || []),
    ]),
  ]
    .filter(Boolean)
    .sort()
    .map((name) => ({ name }));

  const formatDateDisplay = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3 pt-0 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-light d-flex align-items-center justify-content-center"
                style={{ width: "34px", height: "34px", padding: 0, border: "1px solid #E3EBF8", borderRadius: "8px" }}
                title="Back"
              >
                <MdArrowBack size={16} />
              </button>
              <h5 className="mb-0" style={{ fontSize: "18px", fontWeight: "600" }}>
                Calendar
                {selectedDate && (
                  <span style={{ fontSize: "14px", fontWeight: "400", marginLeft: "8px", color: "#6C757D" }}>
                    ({formatDateDisplay(selectedDate)})
                  </span>
                )}
              </h5>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span style={{ fontSize: "14px", color: "#000" }}>Filter by:</span>

              <div ref={dateFilterRef} style={{ position: "relative" }}>
                <button
                  className="btn btn-light d-flex align-items-center justify-content-center"
                  style={{ width: "32px", height: "32px", padding: 0, border: "1px solid #E3EBF8" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                  }}
                >
                  <FaRegCalendarAlt size={14} />
                </button>

                {open && (
                  <div style={{ position: "absolute", top: "40px", right: 0, zIndex: 9999 }}>
                    <DatePicker
                      selected={selectedDate ? new Date(selectedDate) : null}
                      onChange={(date) => {
                        const d1 = date.toLocaleDateString("en-GB").split(/[\/\-.]/);
                        setSelectedDate(`${d1[2]}-${d1[1]}-${d1[0]}`);
                        setOpen(false);
                      }}
                      inline
                    />
                  </div>
                )}
              </div>

              <div style={{ width: isPhone ? "100%" : "240px", flex: isPhone ? "1 1 200px" : undefined, display: "flex", alignItems: "center", gap: "6px" }}>
                <MultiSelect
                  value={selectedProvider}
                  onChange={(e) => onProviderSearch(e.value)}
                  options={allProvider}
                  optionLabel="name"
                  filter
                  placeholder={`Search ${crewLabel}`}
                  maxSelectedLabels={2}
                  style={{
                    flex: 1,
                    height: "40px",
                    backgroundColor: "#F8F9FA",
                    border: "1px solid #E3EBF8",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />

                {selectedProvider.length > 0 && (
                  <button onClick={() => onProviderSearch([])} className="btn btn-danger">
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {isPhone ? (
            <MobileDayAgenda
              events={calenderData}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              onOpen={handleAppointmentClick}
              bookingIdLabel={`${getBookingLabel(industry)} ID`}
              providerLabel={crewLabel.toLowerCase()}
            />
          ) : (
          <div className="calendar-wrapper" data-tour="dashboard-calendar">
            <div className="card border-0">
              <div className="card-body p-34 border-box">
                <div className="position-relative removeHeader dashboardCalender" style={{ minHeight: "720px" }}>
                  <BookingCalendar
                    events={calenderData}
                    crews={selectedProvider.length ? filteredCrews : allCrews}
                    date={selectedDate}
                    datesSet={datesSet}
                    onAppointmentClick={handleAppointmentClick}
                    headerToolbar={true}
                    copy={true}
                    height="max-content"
                    isClinicService={isClinicService}
                    isSportsService={isSportsService}
                    crewLabel={crewLabel}
                    bookingIdLabel={`${getBookingLabel(industry)} ID`}
                  />
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {isClinicService && (
        <AppointmentSidebar
          show={showCanvas}
          onHide={() => setShowCanvas(false)}
          canvasType={canvasType}
          editGroupId={editGroupId}
          selectedDate={selectedDate}
        />
      )}
    </>
  );
};

export default CalendarPage;
